import React, {useEffect, useMemo, useState} from 'react';
import {
    Button,
    Chip,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {searchFintelGaps} from '../../../firebase/firestore/eei';
import SharedTagsInput from './SharedTagsInput';

function normalizeNewTexts(value) {
    return (Array.isArray(value) ? value : [])
        .map((t) => String(t ?? '').trim())
        .filter(Boolean);
}

function normalizeGapIds(value) {
    return (Array.isArray(value) ? value : []).map(String).filter(Boolean);
}

/**
 * ICR EEI editor: create new EEI text and/or promote FINTEL gaps.
 * Does not search/link EEIs from other ICRs.
 *
 * @param {{eeiIds: string[], newEeis: string[], gapIds?: string[], pendingGaps?: object[]}} value
 * @param {(next: object) => void} onChange
 * @param {Array<{id:string,text?:string,eeiNumber?:string}>} [initialSelected]
 * @param {boolean} [allowFindGap] CM/admin gap picker
 */
export default function EeiListInput({
    value = {eeiIds: [], newEeis: [], gapIds: [], pendingGaps: []},
    onChange,
    initialSelected = [],
    allowFindGap = true,
}) {
    const eeiIds = Array.isArray(value?.eeiIds) ? value.eeiIds : [];
    const newEeis = normalizeNewTexts(value?.newEeis);
    const gapIds = normalizeGapIds(value?.gapIds);
    const pendingGaps = Array.isArray(value?.pendingGaps) ? value.pendingGaps : [];
    const [selected, setSelected] = useState(initialSelected);
    const [mode, setMode] = useState('create'); // create | gap
    const [inputValue, setInputValue] = useState('');
    const [tagFilters, setTagFilters] = useState([]);
    const [allGaps, setAllGaps] = useState([]);
    const [gapLoading, setGapLoading] = useState(false);
    const [gapError, setGapError] = useState(null);

    useEffect(() => {
        if (initialSelected?.length) {
            setSelected(initialSelected);
        }
    }, [initialSelected]);

    // Load the gap catalog once when entering Find gap — text/tag filter client-side.
    useEffect(() => {
        if (!allowFindGap || mode !== 'gap') {
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setGapLoading(true);
            setGapError(null);
            try {
                const data = await searchFintelGaps({
                    status: 'Identified,Proposed',
                    limit: 50,
                });
                if (!cancelled) {
                    setAllGaps(Array.isArray(data?.gaps) ? data.gaps : []);
                }
            } catch (e) {
                if (!cancelled) {
                    setGapError(e.message);
                    setAllGaps([]);
                }
            } finally {
                if (!cancelled) setGapLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [allowFindGap, mode]);

    // Tag autocomplete options: tags on loaded gaps + shared-tag prefix suggest
    const gapTagOptions = useMemo(() => {
        const set = new Set();
        for (const g of allGaps) {
            for (const t of Array.isArray(g.tags) ? g.tags : []) {
                const name = String(t || '').trim();
                if (name) set.add(name);
            }
        }
        return [...set].sort((a, b) => a.localeCompare(b));
    }, [allGaps]);

    const selectedGapIdKey = gapIds.join(',');

    const gapResults = useMemo(() => {
        const selectedIds = new Set(selectedGapIdKey ? selectedGapIdKey.split(',') : []);
        const q = inputValue.trim().toLowerCase();
        const requiredTags = tagFilters
            .map((t) => String(t || '').trim().toLowerCase())
            .filter(Boolean);
        return allGaps.filter((g) => {
            if (!g?.id || selectedIds.has(String(g.id))) return false;
            if (q) {
                const hay = `${g.text || ''} ${g.reportTitle || ''} ${g.gapNumber || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (requiredTags.length) {
                const tags = (Array.isArray(g.tags) ? g.tags : [])
                    .map((t) => String(t || '').toLowerCase());
                // AND: gap must carry every selected filter tag
                if (!requiredTags.every((rt) => tags.includes(rt))) return false;
            }
            return true;
        });
    }, [allGaps, inputValue, tagFilters, selectedGapIdKey]);

    const emit = (nextIds, nextNew, nextSelected = selected, nextGapIds = gapIds, nextPending = pendingGaps) => {
        onChange({
            eeiIds: nextIds,
            newEeis: nextNew,
            selectedEeis: nextSelected,
            gapIds: nextGapIds,
            pendingGaps: nextPending,
        });
    };

    const addNew = () => {
        const t = String(inputValue || '').trim();
        if (!t) return;
        if (newEeis.some((x) => x.toLowerCase() === t.toLowerCase())) {
            setInputValue('');
            return;
        }
        if (selected.some((e) => String(e.text || '').trim().toLowerCase() === t.toLowerCase())) {
            setInputValue('');
            return;
        }
        if (pendingGaps.some((g) => String(g.text || '').trim().toLowerCase() === t.toLowerCase())) {
            setInputValue('');
            return;
        }
        emit(eeiIds, [...newEeis, t], selected);
        setInputValue('');
    };

    const addGap = (gap) => {
        if (!gap?.id || gapIds.includes(gap.id)) return;
        const nextGaps = [...pendingGaps, gap];
        const nextIds = [...gapIds, gap.id];
        emit(eeiIds, newEeis, selected, nextIds, nextGaps);
    };

    const removeExisting = (id) => {
        const nextIds = eeiIds.filter((x) => x !== id);
        const nextSelected = selected.filter((e) => e.id !== id);
        setSelected(nextSelected);
        emit(nextIds, newEeis, nextSelected);
    };

    const removeNew = (index) => {
        emit(eeiIds, newEeis.filter((_, i) => i !== index), selected);
    };

    const removeGap = (gapId) => {
        emit(
            eeiIds,
            newEeis,
            selected,
            gapIds.filter((id) => id !== gapId),
            pendingGaps.filter((g) => g.id !== gapId),
        );
    };

    const includedCount = selected.length + newEeis.length + pendingGaps.length;

    return (
        <Stack spacing={1.5}>
            <Typography variant="h6">EEIs</Typography>
            <Typography variant="body2" color="text.secondary">
                Add new EEIs for this ICR, or convert a FINTEL gap into an EEI on save.
                EEIs are scoped to a single ICR.
            </Typography>

            {allowFindGap && (
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={mode}
                    onChange={(_, next) => {
                        if (next) {
                            setMode(next);
                            setInputValue('');
                            setTagFilters([]);
                            setGapError(null);
                        }
                    }}
                >
                    <ToggleButton value="create">Create new</ToggleButton>
                    <ToggleButton value="gap">Find gap</ToggleButton>
                </ToggleButtonGroup>
            )}

            {mode === 'create' || !allowFindGap ? (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                        size="small"
                        fullWidth
                        label="Create new EEI"
                        placeholder="Type an EEI and press Add…"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addNew();
                            }
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon/>}
                        onClick={addNew}
                        disabled={!inputValue.trim()}
                        sx={{flexShrink: 0, height: 40}}
                    >
                        Add
                    </Button>
                </Stack>
            ) : (
                <Stack spacing={1}>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems="flex-start">
                        <TextField
                            fullWidth
                            label="Filter by text"
                            placeholder="Gap question, GAP ID, or FINTEL title…"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                        />
                        <SharedTagsInput
                            label="Filter by tag"
                            placeholder="Add tag…"
                            value={tagFilters}
                            onChange={setTagFilters}
                            extraOptions={gapTagOptions}
                        />
                    </Stack>
                    {gapError && (
                        <Typography variant="caption" color="error">{gapError}</Typography>
                    )}
                    {gapLoading && (
                        <Typography variant="caption" color="text.secondary">Loading gaps…</Typography>
                    )}
                    {!gapLoading && gapResults.length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                            No Identified/Proposed gaps match these filters.
                        </Typography>
                    )}
                    <List dense sx={{maxHeight: 280, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1}}>
                        {gapResults.map((gap) => {
                            const tags = Array.isArray(gap.tags) ? gap.tags.filter(Boolean) : [];
                            return (
                                <ListItemButton key={gap.id} onClick={() => addGap(gap)} alignItems="flex-start">
                                    <ListItemText
                                        primary={gap.text}
                                        secondary={
                                            <Stack spacing={0.5} sx={{mt: 0.5}}>
                                                {gap.gapNumber && (
                                                    <Typography
                                                        variant="caption"
                                                        color="primary"
                                                        fontFamily="monospace"
                                                        component="span"
                                                    >
                                                        {gap.gapNumber}
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" color="text.secondary" component="span">
                                                    {[gap.status, gap.reportTitle].filter(Boolean).join(' · ')}
                                                </Typography>
                                                {tags.length > 0 && (
                                                    <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap>
                                                        {tags.map((t) => (
                                                            <Chip
                                                                key={t}
                                                                size="small"
                                                                label={t}
                                                                variant="outlined"
                                                                sx={{height: 22, '& .MuiChip-label': {px: 0.75, fontSize: '0.7rem'}}}
                                                            />
                                                        ))}
                                                    </Stack>
                                                )}
                                            </Stack>
                                        }
                                        primaryTypographyProps={{variant: 'body2'}}
                                        secondaryTypographyProps={{component: 'div'}}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                </Stack>
            )}

            {includedCount > 0 && (
                <Stack spacing={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Included EEIs ({includedCount})
                    </Typography>
                    {selected.map((eei) => (
                        <Paper key={eei.id} variant="outlined" sx={{p: 1.25}}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Stack spacing={0.25} sx={{flex: 1, minWidth: 0}}>
                                    <Typography variant="caption" color="primary" fontFamily="monospace">
                                        {eei.eeiNumber || eei.id}
                                    </Typography>
                                    <Typography variant="body2" sx={{lineHeight: 1.45}}>
                                        {eei.text || '—'}
                                    </Typography>
                                </Stack>
                                <IconButton
                                    aria-label={`Remove ${eei.eeiNumber || 'EEI'}`}
                                    onClick={() => removeExisting(eei.id)}
                                    size="small"
                                >
                                    <DeleteOutlineIcon fontSize="small"/>
                                </IconButton>
                            </Stack>
                        </Paper>
                    ))}
                    {newEeis.map((text, index) => (
                        <Paper key={`new-${index}-${text}`} variant="outlined" sx={{p: 1.25}}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Stack spacing={0.25} sx={{flex: 1, minWidth: 0}}>
                                    <Typography variant="caption" color="text.secondary">
                                        New
                                    </Typography>
                                    <Typography variant="body2" sx={{lineHeight: 1.45}}>
                                        {text}
                                    </Typography>
                                </Stack>
                                <IconButton
                                    aria-label={`Remove new EEI ${index + 1}`}
                                    onClick={() => removeNew(index)}
                                    size="small"
                                >
                                    <DeleteOutlineIcon fontSize="small"/>
                                </IconButton>
                            </Stack>
                        </Paper>
                    ))}
                    {pendingGaps.map((gap) => (
                        <Paper key={`gap-${gap.id}`} variant="outlined" sx={{p: 1.25}}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Stack spacing={0.25} sx={{flex: 1, minWidth: 0}}>
                                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Chip size="small" label="From gap" color="warning" variant="outlined"/>
                                        {gap.gapNumber && (
                                            <Typography variant="caption" color="primary" fontFamily="monospace">
                                                {gap.gapNumber}
                                            </Typography>
                                        )}
                                        {gap.status && (
                                            <Typography variant="caption" color="text.secondary">
                                                {gap.status}
                                            </Typography>
                                        )}
                                    </Stack>
                                    <Typography variant="body2" sx={{lineHeight: 1.45}}>
                                        {gap.text || '—'}
                                    </Typography>
                                    {gap.reportTitle && (
                                        <Typography variant="caption" color="text.secondary">
                                            {gap.reportTitle}
                                        </Typography>
                                    )}
                                    {Array.isArray(gap.tags) && gap.tags.length > 0 && (
                                        <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap sx={{pt: 0.25}}>
                                            {gap.tags.map((t) => (
                                                <Chip key={t} size="small" label={t} variant="outlined"/>
                                            ))}
                                        </Stack>
                                    )}
                                </Stack>
                                <IconButton
                                    aria-label="Remove gap EEI"
                                    onClick={() => removeGap(gap.id)}
                                    size="small"
                                >
                                    <DeleteOutlineIcon fontSize="small"/>
                                </IconButton>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}
        </Stack>
    );
}
